/* eslint-disable  @typescript-eslint/no-explicit-any */
'use client';

import {useEffect, useRef, useState} from 'react';
import * as THREE from 'three';
import {OrbitControls} from 'three/examples/jsm/controls/OrbitControls.js';
import c from '@/data/colors.json';
// import {RGBELoader} from 'three/examples/jsm/loaders/RGBELoader.js';
import {FBXLoader} from 'three/examples/jsm/loaders/FBXLoader.js';
import {EffectComposer} from 'three/examples/jsm/postprocessing/EffectComposer.js';
import {RenderPass} from 'three/examples/jsm/postprocessing/RenderPass.js';
import {UnrealBloomPass} from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import {SMAAPass} from 'three/examples/jsm/postprocessing/SMAAPass.js';

interface Color {
    code: string;
    name: string;
    rgb: string;
    hex: string;
}

interface SavedConfiguration {
    name: string;
    clientName: string;
    frontColor: string;
    backColor: string;
    piedColor: string;
    plateauColor: string;
    logoUrl: string;
    logoIntensity: number;
    logoScale: number;
    logoPositionX: number;
    logoPositionY: number;
    logoThreshold: number;
    date: string;
}

export default function ModelConfigurator() {
    const viewerRef = useRef<any>(null);
    const [isClient, setIsClient] = useState(false);
    const [isModelLoaded, setIsModelLoaded] = useState(false);

    // États pour les contrôles
    const [logoIntensity, setLogoIntensity] = useState(1);
    const [logoScale, setLogoScale] = useState(0.6);
    const [logoPositionX, setLogoPositionX] = useState(0);
    const [logoPositionY, setLogoPositionY] = useState(0);
    const [logoThreshold, setLogoThreshold] = useState(240);
    const [currentLogoUrl, setCurrentLogoUrl] = useState('');

    const [clientName, setClientName] = useState('');
    const [configName, setConfigName] = useState('');
    const [savedConfigurations, setSavedConfigurations] = useState<SavedConfiguration[]>([]);
    const [showSaveModal, setShowSaveModal] = useState(false);


    // Référence aux objets Three.js
    const sceneRef = useRef<any>(null);
    const cameraRef = useRef<any>(null);
    const rendererRef = useRef<any>(null);
    const controlsRef = useRef<any>(null);

    const dozGroupRef = useRef<any>(null);
    const logoGroupRef = useRef<any>(null);
    const tableGroupRef = useRef<any>(null);

// Références pour les deux parties du modèle du DOZ
    const frontModelRef = useRef<any>(null);
    const backModelRef = useRef<any>(null);

    const carlaModelRef = useRef<any>(null);

// Références pour les deux parties du modèle de la table
    const piedModelRef = useRef<any>(null);
    // const envMapRef = useRef<any>(null);

    const [frontModelColor, setFrontModelColor] = useState('#FACA30');
    const [backModelColor, setBackModelColor] = useState('#2F3133');
    const [piedModelColor, setPiedModelColor] = useState('#2F3133');
    const [plateauModelColor, setPlateauModelColor] = useState('#FACA30');

    const [colorSearchTerm, setColorSearchTerm] = useState('');
    const [isColorDropdownOpen, setIsColorDropdownOpen] = useState(false);
    const [activeColorSelector, setActiveColorSelector] = useState<'front' | 'back' | 'plateau' | 'pied' | null>(null);
    const colorDropdownRef = useRef<HTMLDivElement>(null);

    const [showMainDoz, setShowMainDoz] = useState(true);
    const [showLeftDoz, setShowLeftDoz] = useState(true);
    const [showRightDoz, setShowRightDoz] = useState(true);
    const [showTable, setShowTable] = useState(true);
    const [showPersonnage, setShowPersonnage] = useState(true);
    const [showLogos, setShowLogos] = useState(true);
    const [duplicateLogoGroups, setDuplicateLogoGroups] = useState<THREE.Group[]>([]);

    const colors: Record<string, Color> = c;

    const [duplicateModels, setDuplicateModels] = useState<THREE.Group[]>([]);


    const [mainLightsEnabled, setMainLightsEnabled] = useState(true);
    const [mainLightIntensity, setMainLightIntensity] = useState(3.1);

    const [ambiantLightsEnabled, setAmbiantLightsEnabled] = useState(true);
    const [ambiantLightIntensity, setAmbiantLightIntensity] = useState(2.8);

    const [sphereLightsEnabled, setSphereLightsEnabled] = useState(true);
    const [sphereLightIntensity, setSphereLightIntensity] = useState(0);

    const lightsRefMain = useRef<THREE.Light[]>([]);
    const lightsRefAmbiant = useRef<THREE.Light[]>([]);
    const lightsRefSphere = useRef<THREE.Light[]>([]);

    const loader = new FBXLoader();
    const [duplicatePositions] = useState([
        {x: -0.7, y: 0, z: 1.8},  // Position du premier duplicata
        {x: 0.7, y: 0, z: 1.8}    // Position du deuxième duplicata
    ]);

    const [duplicateRotations] = useState([
        {x: 0, y: 3 * Math.PI / 4, z: 0},  // Rotation du premier duplicata
        {x: 0, y: 5 * Math.PI / 4, z: 0}
    ]);

    const [cameraPositions] = useState([
        { position: { x: 3.27, y: 1.31, z: 2.94  }, target: { x: -0.3, y:  -0.55, z: 0.44 } },
        { position: { x: -1.58, y:  1.2, z: -1.69 }, target: { x: -0.11, y: 0.36, z: 1.02 } },
    ]);

    const screenshotButtonRef = useRef<any>(null);

// URLs des modèles GLTF du DOZ
    const frontModelUrl = '/assets/model/dozuv.fbx';

// URLs des modèles GLTF de la table
    const piedModelUrl = '/assets/model/table2+.fbx';

    // Initialisation côté client uniquement
    useEffect(() => {
        setIsClient(true);
    }, []);

    useEffect(() => {
        if (isClient) {
            const savedConfigs = localStorage.getItem('dozConfigurations');
            if (savedConfigs) {
                setSavedConfigurations(JSON.parse(savedConfigs));
            }
        }
    }, [isClient]);


    // Initialisation de Three.js
    useEffect(() => {
        if (!isClient || !viewerRef.current) return;

        // Création de la scène
        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0xf0f0f0);
        sceneRef.current = scene;

        // Création de la caméra
        const camera = new THREE.PerspectiveCamera(75, viewerRef.current.clientWidth / window.innerHeight, 0.1, 1000);
        camera.position.set(0, 3, 4);
        cameraRef.current = camera;

        // Création du renderer
        const renderer = new THREE.WebGLRenderer({antialias: true, preserveDrawingBuffer: true});
        renderer.setSize(viewerRef.current.clientWidth, window.innerHeight);
        renderer.setPixelRatio(window.devicePixelRatio);
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        viewerRef.current.appendChild(renderer.domElement);
        rendererRef.current = renderer;
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1.2;

        // const pmremGenerator = new THREE.PMREMGenerator(renderer);
        // new RGBELoader()
        //     .load('/assets/envMap/clouds.hdr', function (texture: any) {
        //         texture.mapping = THREE.EquirectangularReflectionMapping;
        //         envMapRef.current = pmremGenerator.fromEquirectangular(texture).texture;
        //         // if (showEnvMap)
        //         //     scene.background = texture;
        //         // texture.dispose();
        //         // pmremGenerator.dispose();
        //     });

        const composer = new EffectComposer(renderer);

// 1. RenderPass : rend la scène normalement
        composer.addPass(new RenderPass(scene, camera));

// 2. BloomPass : ajoute un effet d'éclat lumineux
        const bloomPass = new UnrealBloomPass(
            new THREE.Vector2(window.innerWidth, window.innerHeight),
            0.2, // strength
            0.2, // radius
            0.85 // threshold
        );
        composer.addPass(bloomPass);

// 3. SMAA : antialiasing de post-process
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const smaaPass = new SMAAPass(window.innerWidth, window.innerHeight);
        composer.addPass(smaaPass);

        screenshotButtonRef.current = addScreenshotbutton();
        addCameraPositionDisplay();

        // Contrôles de la caméra
        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.05;
        controls.minDistance = 0.5;
        controls.maxDistance = 15;
        controlsRef.current = controls;
        const gl = renderer.getContext();
        console.log('MAX_TEXTURE_SIZE:', gl.getParameter(gl.MAX_TEXTURE_SIZE));
        console.log('MAX_VERTEX_ATTRIBS:', gl.getParameter(gl.MAX_VERTEX_ATTRIBS));
        // Éclairage
        addLight(scene);

        loadDoz();


        // const axesHelper = new THREE.AxesHelper( 5 );
        // scene.add( axesHelper );

        // Animation
        const animate = () => {
            controls.update();
            renderer.render(scene, camera);

            requestAnimationFrame(animate);
            composer.render();

        };

        requestAnimationFrame(animate);


        // Gestion du redimensionnement de la fenêtre
        const handleResize = () => {
            if (!viewerRef.current) return;
            camera.aspect = viewerRef.current.clientWidth / window.innerHeight;
            camera.updateProjectionMatrix();
            renderer.setSize(viewerRef.current.clientWidth, window.innerHeight);
        };

        window.addEventListener('resize', handleResize);

        // Nettoyage
        return () => {
            window.removeEventListener('resize', handleResize);
            if (viewerRef.current && renderer.domElement) {
                viewerRef.current.removeChild(renderer.domElement);
            }
            renderer.dispose();
        };
    }, [isClient]);

    useEffect(() => {
        if (!screenshotButtonRef.current) return;

        // Supprimer les anciens écouteurs d'événements
        const oldButton = screenshotButtonRef.current;
        const newButton = oldButton.cloneNode(true);
        oldButton.parentNode.replaceChild(newButton, oldButton);
        screenshotButtonRef.current = newButton;

        // Ajouter le nouvel écouteur avec la valeur actuelle de clientName
        newButton.addEventListener('click', () => {
            // Prendre la capture d'écran
            const dataURL = rendererRef.current.domElement.toDataURL('image/jpeg');

            // Créer un lien de téléchargement
            const link = document.createElement('a');
            link.href = dataURL;
            const filename = clientName ? `${clientName}.jpeg` : 'doz-configuration.jpeg';
            link.download = filename;
            link.click();
        });
    }, [clientName]);

    // Mise à jour des couleurs des modèles
    useEffect(() => {
        if (!frontModelRef.current && !backModelRef.current) return;

        // Appliquer la couleur au modèle avant
        if (frontModelRef.current) {
            frontModelRef.current.traverse((node: any) => {
                if (node.isMesh && node.material && node.name === "front") {
                    node.material.color.set(frontModelColor);
                }
            });
        }
// Mettre à jour le logo si présent
        if (currentLogoUrl) {
            applyLogoDecal(currentLogoUrl);
        }
    }, [frontModelColor]);

    useEffect(() => {
        if (!backModelRef.current) return;

        // Appliquer la couleur au modèle arrière
        backModelRef.current.traverse((node: any) => {
            if (node.isMesh && node.material && node.name === "back") {
                // Ne pas modifier la partie si un logo est appliqué
                node.material.color.set(backModelColor);
            }
        });
        // Mettre à jour le logo si présent
        if (currentLogoUrl) {
            applyLogoDecal(currentLogoUrl);
        }
    }, [backModelColor]);

    useEffect(() => {
        if (!tableGroupRef.current) return;

        tableGroupRef.current.traverse((node: any) => {
            if (node.isMesh && node.material && node.name === "pied") {
                node.material.color.set(piedModelColor);
            }
        });
    }, [piedModelColor]);

    useEffect(() => {
        if (!tableGroupRef.current) return;

        tableGroupRef.current.traverse((node: any) => {
            console.log(node.isMesh)
            console.log(node.material)
            console.log(node.name)
            if (node.isMesh && node.material && node.name === "plateau") {
                node.material.color.set(plateauModelColor);
            }
        });
    }, [plateauModelColor]);
    // Effet pour mettre à jour le logo quand le seuil change
    useEffect(() => {
        if (currentLogoUrl) {
            applyLogoDecal(currentLogoUrl);
        }
    }, [logoThreshold]);

    // Filtrer les couleurs en fonction du terme de recherche
    const filteredColors = Object.values(colors).filter(color =>
        color.name.toLowerCase().includes(colorSearchTerm.toLowerCase()) ||
        color.code.includes(colorSearchTerm)
    );

    // Fermer le dropdown quand on clique ailleurs
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (colorDropdownRef.current && !colorDropdownRef.current.contains(event.target as Node)) {
                setIsColorDropdownOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);


    useEffect(() => {
        if (dozGroupRef.current) {
            dozGroupRef.current.visible = showMainDoz;
        }
    }, [showMainDoz]);

    useEffect(() => {
        if (duplicateModels[0]) {
            duplicateModels[0].visible = showLeftDoz;
        }
    }, [showLeftDoz, duplicateModels]);

    useEffect(() => {
        if (duplicateModels[1]) {
            duplicateModels[1].visible = showRightDoz;
        }
    }, [showRightDoz, duplicateModels]);

    useEffect(() => {
        if (tableGroupRef.current) {
            tableGroupRef.current.visible = showTable;
        }
    }, [showTable]);

    // useEffect(() => {
    //     if (envMapRef.current) {
    //         if (showEnvMap) {
    //             // sceneRef.current.background = envMapRef.current;
    //             sceneRef.current.environment = envMapRef.current;
    //         } else {
    //             sceneRef.current.background = new THREE.Color(0xf0f0f0);
    //             sceneRef.current.environment = null;
    //         }
    //
    //     }
    // }, [showEnvMap]);

    useEffect(() => {
        if (carlaModelRef.current) {
            carlaModelRef.current.visible = showPersonnage;
        }
    }, [showPersonnage]);

    useEffect(() => {
        if (logoGroupRef.current) {
            logoGroupRef.current.visible = showLogos;
        }

        duplicateLogoGroups.forEach(group => {
            if (group) {
                group.visible = showLogos;
            }
        });
    }, [showLogos, duplicateLogoGroups]);

    // Fonction pour sauvegarder la configuration actuelle
    const saveCurrentConfiguration = () => {
        if (!configName) return;

        const newConfig: SavedConfiguration = {
            name: configName,
            clientName: clientName,
            frontColor: frontModelColor,
            backColor: backModelColor,
            piedColor: piedModelColor,
            plateauColor: plateauModelColor,
            logoUrl: currentLogoUrl,
            logoIntensity: logoIntensity,
            logoScale: logoScale,
            logoPositionX: logoPositionX,
            logoPositionY: logoPositionY,
            logoThreshold: logoThreshold,
            date: new Date().toLocaleString()
        };

        const updatedConfigs = [...savedConfigurations, newConfig];
        setSavedConfigurations(updatedConfigs);
        localStorage.setItem('dozConfigurations', JSON.stringify(updatedConfigs));

        setShowSaveModal(false);
        setConfigName('');
    };

    // Fonction pour charger une configuration
    const loadConfiguration = (config: SavedConfiguration) => {

        setCurrentLogoUrl('');

        setClientName(config.clientName);
        setFrontModelColor(config.frontColor);
        setBackModelColor(config.backColor);
        setPiedModelColor(config.piedColor);
        setPlateauModelColor(config.plateauColor);

        setLogoIntensity(config.logoIntensity);
        setLogoScale(config.logoScale);
        setLogoPositionX(config.logoPositionX);
        setLogoPositionY(config.logoPositionY);
        // Charger le seuil du logo s'il existe
        if (config.logoThreshold !== undefined) {
            setLogoThreshold(config.logoThreshold);
        }
        // Charger le logo si présent
        if (config.logoUrl) {
            setLogoScale(config.logoScale);
            setCurrentLogoUrl(config.logoUrl);
        }
    };

    // Fonction pour supprimer une configuration
    const deleteConfiguration = (index: number) => {
        const updatedConfigs = [...savedConfigurations];
        updatedConfigs.splice(index, 1);
        setSavedConfigurations(updatedConfigs);
        localStorage.setItem('dozConfigurations', JSON.stringify(updatedConfigs));
    };

    const createDuplicates = () => {
        if (!dozGroupRef.current) return;

        // Supprimer les duplicatas existants
        duplicateModels.forEach(model => {
            sceneRef.current.remove(model);
        });

        const newDuplicates: THREE.Group[] = [];

        // Créer deux duplicatas
        for (let i = 0; i < 2; i++) {
            // Cloner le modèle original
            const duplicate = dozGroupRef.current.clone(true);

            // Positionner le duplicata
            duplicate.position.set(
                duplicatePositions[i].x,
                duplicatePositions[i].y,
                duplicatePositions[i].z
            );

            duplicate.rotation.set(
                duplicateRotations[i].x,
                duplicateRotations[i].y,
                duplicateRotations[i].z
            );

            // Ajouter à la scène
            sceneRef.current.add(duplicate);
            newDuplicates.push(duplicate);
        }

        setDuplicateModels(newDuplicates);
    };

    useEffect(() => {
        if (isModelLoaded && dozGroupRef.current) {
            loadTable()
            createDuplicates();
            loadCarlaModel();
        }
    }, [isModelLoaded]);

    useEffect(() => {
        duplicateModels.forEach(duplicate => {
            duplicate.traverse((node: any) => {
                if (node.isMesh && node.material) {
                    // Déterminer si c'est une partie avant
                    const isFrontPart = node.name.includes('front') || node.position.z > 0;
                    if (isFrontPart) {
                        node.material.color.set(frontModelColor);
                    }
                }
            });
        });
    }, [frontModelColor, duplicateModels]);

    useEffect(() => {
        duplicateModels.forEach(duplicate => {
            duplicate.traverse((node: any) => {
                if (node.isMesh && node.material) {
                    // Déterminer si c'est une partie arrière
                    const isBackPart = node.name.includes('back');
                    if (isBackPart) {
                        node.material.color.set(backModelColor);
                    }
                }
            });
        });
    }, [backModelColor, duplicateModels]);

    useEffect(() => {
        if (currentLogoUrl) {
            applyLogoDecal(currentLogoUrl);
        }
    }, [logoIntensity, logoScale, logoPositionX, logoPositionY]);

    useEffect(() => {
        if (lightsRefMain.current.length === 0) return;

        lightsRefMain.current.forEach((light: any) => {
            light.intensity = mainLightsEnabled ? mainLightIntensity : 0;
        });
    }, [mainLightIntensity, mainLightsEnabled]);

    useEffect(() => {
        if (lightsRefAmbiant.current.length === 0) return;

        lightsRefAmbiant.current.forEach((light: any) => {
            light.intensity = ambiantLightsEnabled ? ambiantLightIntensity : 0;
        });
    }, [ambiantLightIntensity, ambiantLightsEnabled]);

    useEffect(() => {
        if (lightsRefSphere.current.length === 0) return;

        lightsRefSphere.current.forEach((light: any) => {
            light.intensity = sphereLightsEnabled ? sphereLightIntensity : 0;
        });
    }, [sphereLightIntensity, sphereLightsEnabled]);



    // Sélectionner une couleur
    const selectColor = (hex: string) => {
        if (activeColorSelector === 'front') {
            setFrontModelColor(hex);
        } else if (activeColorSelector === 'back') {
            setBackModelColor(hex);
        } else if (activeColorSelector === 'pied') {
            setPiedModelColor(hex);
        } else if (activeColorSelector === 'plateau') {
            setPlateauModelColor(hex);
        }
        setIsColorDropdownOpen(false);
    };

    // Ouvrir le sélecteur de couleur
    const openColorSelector = (type: 'front' | 'back' | 'plateau' | 'pied') => {
        setActiveColorSelector(type);
        setIsColorDropdownOpen(true);
        setColorSearchTerm('');
    };


    // Fonction pour prétraiter l'image et enlever l'arrière-plan
    const processLogoImage = (imageUrl: string, callback: any) => {
        const img = new Image();
        img.crossOrigin = "Anonymous";

        img.onload = () => {
            // Créer un canvas pour manipuler l'image
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                return;
            }
            // Dessiner l'image sur le canvas
            ctx.drawImage(img, 0, 0);

            // Obtenir les données de l'image
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const data = imageData.data;

            // Définir une couleur de fond à considérer comme transparente (blanc ou proche du blanc)
            const threshold = logoThreshold; // Seuil pour considérer un pixel comme blanc

            // Parcourir tous les pixels
            for (let i = 0; i < data.length; i += 4) {
                const r = data[i];
                const g = data[i + 1];
                const b = data[i + 2];

                // Si le pixel est proche du blanc, le rendre transparent
                if (r > threshold && g > threshold && b > threshold) {
                    data[i + 3] = 0; // Canal alpha à 0 (transparent)
                } else {

                    // Puis convertir en blanc pur pour les zones non transparentes
                    data[i] = 255;     // R
                    data[i + 1] = 255; // G
                    data[i + 2] = 255; // B
                    // Conserver l'alpha d'origine
                }
            }

            // Mettre à jour l'image avec les nouvelles données
            ctx.putImageData(imageData, 0, 0);

            // Convertir le canvas en URL de données
            const processedImageUrl = canvas.toDataURL('image/png');

            // Appeler le callback avec l'URL de l'image traitée
            callback(processedImageUrl);
        };

        img.src = imageUrl;
    };

// Fonction pour appliquer le logo comme décalcomanie (decal) sur les modèles
    const applyLogoDecal = (logoUrl: string) => {
        if (!sceneRef.current || !dozGroupRef.current) {
            console.warn("La scène ou les modèles ne sont pas chargés");
            return;
        }

        // Supprimer les décals existants
        const decalsToRemove: any[] = [];
        sceneRef.current.traverse((object: any) => {
            if (object.userData && object.userData.isLogoDecal) {
                decalsToRemove.push(object);
            }
        });

        decalsToRemove.forEach(decal => {
            if (decal.parent) {
                decal.parent.remove(decal);
            }
        });

        // Charger l'image du logo comme masque alpha
        const textureLoader = new THREE.TextureLoader();
        textureLoader.load(logoUrl, (logoTexture: any) => {
            // Mettre à jour l'aperçu du logo
            updateLogoPreview(logoUrl);

            // Récupérer les couleurs actuelles du DOZ
            const frontColor = new THREE.Color(frontModelColor);
            const backColor = new THREE.Color(backModelColor);

            const textureLoader = new THREE.TextureLoader();

            const metalnessMap = textureLoader.load('/assets/textures/doz/Poliigon_MetalGalvanizedZinc_7184_Metallic.jpg');
            const roughnessMap = textureLoader.load('/assets/textures/doz/Poliigon_MetalGalvanizedZinc_7184_Roughness.jpg');
            const normalMap = textureLoader.load('/assets/textures/doz/Poliigon_MetalGalvanizedZinc_7184_Normal.png'); // ou NormalGL selon le format
            const displacementMap = textureLoader.load('/assets/textures/doz/Poliigon_MetalGalvanizedZinc_7184_Displacement.tiff');

            const frontDecalMaterial = new THREE.MeshPhysicalMaterial({
                color: backColor,
                alphaMap: logoTexture,
                transparent: true,
                opacity: logoIntensity,
                metalnessMap: metalnessMap,
                roughnessMap: roughnessMap,
                normalMap: normalMap,
                displacementMap: displacementMap,
                metalness: 0.3,  // Effet métallisé
                roughness: 0.7,  // Surface lisse pour plus de brillance
                clearcoat: 0.5,  // Effet de vernis
                clearcoatRoughness: 0.2,
                depthTest: true,
                depthWrite: false,
                side: THREE.DoubleSide
            });

            // Créer le matériau pour le logo avant (avec la couleur arrière) TODO changer la texture
            // const frontDecalMaterial = new THREE.MeshPhysicalMaterial({
            //     color: backColor, // Utiliser la couleur arrière pour le logo avant
            //     alphaMap: logoTexture,
            //     transparent: true,
            //     opacity: logoIntensity,
            //     metalness: 0.3,  // Effet métallisé
            //     roughness: 0.7,  // Surface lisse pour plus de brillance
            //     clearcoat: 0.5,  // Effet de vernis
            //     clearcoatRoughness: 0.2,
            //     depthTest: true,
            //     depthWrite: false,
            //     side: THREE.DoubleSide
            // });


            const backDecalMaterial = new THREE.MeshPhysicalMaterial({
                color: frontColor,
                alphaMap: logoTexture,
                transparent: true,
                opacity: logoIntensity,
                metalnessMap: metalnessMap,
                roughnessMap: roughnessMap,
                normalMap: normalMap,
                displacementMap: displacementMap,
                metalness: 0.3,  // Effet métallisé
                roughness: 0.7,  // Surface lisse pour plus de brillance
                clearcoat: 0.5,  // Effet de vernis
                clearcoatRoughness: 0.2,
                depthTest: true,
                depthWrite: false,
                side: THREE.DoubleSide
            });

            // Créer le matériau pour le logo arrière (avec la couleur avant)
            // const backDecalMaterial = new THREE.MeshPhysicalMaterial({
            //     color: frontColor, // Utiliser la couleur avant pour le logo arrière
            //     alphaMap: logoTexture,
            //     transparent: true,
            //     opacity: logoIntensity,
            //     metalness: 0.3,
            //     roughness: 0.7,
            //     clearcoat: 0.5,
            //     clearcoatRoughness: 0.2,
            //     depthTest: true,
            //     depthWrite: false,
            //     side: THREE.DoubleSide
            // });

            // Taille du décal basée sur l'échelle du logo
            const decalWidth = 0.5 * logoScale;
            const decalHeight = 0.5 * logoScale;
            const decalGeometry = new THREE.PlaneGeometry(decalWidth, decalHeight);

            // Créer les décals
            const frontDecal = new THREE.Mesh(decalGeometry, frontDecalMaterial);
            const backDecal = new THREE.Mesh(decalGeometry, backDecalMaterial);

            // Marquer comme décals de logo
            frontDecal.userData.isLogoDecal = true;
            backDecal.userData.isLogoDecal = true;

            // Positionner le décal avant
            frontDecal.position.set(logoPositionX, -0.3, -0.245);
            frontDecal.rotation.x = Math.PI * 15 / 180;
            // Positionner le décal arrière et l'incliner de 15 degrés
            backDecal.position.set(logoPositionX, -0.05, -0.200);
            backDecal.rotation.y = Math.PI; // Retourner pour faire face à l'arrière
            backDecal.rotation.x = Math.PI * 15 / 180; // Incliner de 15 degrés


            // Ajouter les decals au group des logos

            if (!logoGroupRef.current) {
                const logoGroup = new THREE.Group();
                logoGroupRef.current = logoGroup;
                dozGroupRef.current.add(logoGroup);
            }

            // Supprimer tous les enfants existants du groupe de logos
            while (logoGroupRef.current.children.length > 0) {
                logoGroupRef.current.remove(logoGroupRef.current.children[0]);
            }

            logoGroupRef.current.add(frontDecal);
            logoGroupRef.current.add(backDecal);

            const newDuplicateLogoGroups: THREE.Group[] = [];

            // Appliquer sur les duplicatas
            duplicateModels.forEach(model => {
                const dupFrontDecal = frontDecal.clone();
                const dupBackDecal = backDecal.clone();

                // Créer un groupe de logos pour ce duplicata
                const dupLogoGroup = new THREE.Group();
                dupLogoGroup.add(dupFrontDecal);
                dupLogoGroup.add(dupBackDecal);

                // Ajouter le groupe au modèle
                model.add(dupLogoGroup);

                // Stocker la référence au groupe
                newDuplicateLogoGroups.push(dupLogoGroup);
            });
            setDuplicateLogoGroups(newDuplicateLogoGroups);
            setCurrentLogoUrl(logoUrl);
        });
    };


    const addScreenshotbutton = () => {
        // Créer le bouton de capture d'écran
        const screenshotButton = document.createElement('button');
        screenshotButton.innerHTML = '<i class="fas fa-camera"></i>';
        screenshotButton.title = "Prendre une capture d'écran";

        // Styliser le bouton
        screenshotButton.style.position = 'absolute';
        screenshotButton.style.top = '15px';
        screenshotButton.style.right = '15px';
        screenshotButton.style.zIndex = '100';
        screenshotButton.style.backgroundColor = '#3498db';
        screenshotButton.style.color = 'white';
        screenshotButton.style.border = 'none';
        screenshotButton.style.borderRadius = '50%';
        screenshotButton.style.width = '50px';
        screenshotButton.style.height = '50px';
        screenshotButton.style.fontSize = '20px';
        screenshotButton.style.cursor = 'pointer';
        screenshotButton.style.boxShadow = '0 2px 5px rgba(0,0,0,0.3)';
        screenshotButton.style.transition = 'all 0.3s ease';

        // Ajouter au DOM
        document.body.appendChild(screenshotButton);

        // Ajouter les boutons de position de caméra
        cameraPositions.forEach((pos, index) => {
            const cameraButton = document.createElement('button');
            cameraButton.innerHTML = `${index + 1}`;
            cameraButton.title = `Vue ${index + 1}`;

            // Styliser le bouton
            cameraButton.style.position = 'absolute';
            cameraButton.style.top = `${75 + (index * 60)}px`;
            cameraButton.style.right = '15px';
            cameraButton.style.zIndex = '100';
            cameraButton.style.backgroundColor = '#e74c3c';
            cameraButton.style.color = 'white';
            cameraButton.style.border = 'none';
            cameraButton.style.borderRadius = '50%';
            cameraButton.style.width = '50px';
            cameraButton.style.height = '50px';
            cameraButton.style.fontSize = '20px';
            cameraButton.style.fontWeight = 'bold';
            cameraButton.style.cursor = 'pointer';
            cameraButton.style.boxShadow = '0 2px 5px rgba(0,0,0,0.3)';
            cameraButton.style.transition = 'all 0.3s ease';

            // Ajouter au DOM
            document.body.appendChild(cameraButton);

            // Ajouter l'événement de clic
            cameraButton.addEventListener('click', () => {
                // Configurer la caméra avec les coordonnées
                if (cameraRef.current && controlsRef.current) {
                    cameraRef.current.position.set(
                        pos.position.x,
                        pos.position.y,
                        pos.position.z
                    );
                    controlsRef.current.target.set(
                        pos.target.x,
                        pos.target.y,
                        pos.target.z
                    );
                    controlsRef.current.update();
                }
            });
        });

        return screenshotButton;
    }

    const addCameraPositionDisplay = () => {
        // Créer l'élément d'affichage
        const positionDisplay = document.createElement('div');

        // Styliser l'élément
        positionDisplay.style.position = 'absolute';
        positionDisplay.style.bottom = '15px';
        positionDisplay.style.right = '15px';
        positionDisplay.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
        positionDisplay.style.color = 'white';
        positionDisplay.style.padding = '10px';
        positionDisplay.style.borderRadius = '5px';
        positionDisplay.style.fontFamily = 'monospace';
        positionDisplay.style.fontSize = '12px';
        positionDisplay.style.zIndex = '100';
        positionDisplay.style.pointerEvents = 'none'; // Pour ne pas interférer avec les contrôles

        // Ajouter au DOM
        document.body.appendChild(positionDisplay);

        // Fonction de mise à jour de l'affichage
        const updatePositionDisplay = () => {
            if (cameraRef.current && controlsRef.current) {
                const camera = cameraRef.current;
                const target = controlsRef.current.target;

                // Formater les positions avec 2 décimales
                const posX = camera.position.x.toFixed(2);
                const posY = camera.position.y.toFixed(2);
                const posZ = camera.position.z.toFixed(2);

                const targetX = target.x.toFixed(2);
                const targetY = target.y.toFixed(2);
                const targetZ = target.z.toFixed(2);

                // Mettre à jour le texte
                positionDisplay.innerHTML = `
        <strong>Camera:</strong> x: ${posX}, y: ${posY}, z: ${posZ}<br>
        <strong>Target:</strong> x: ${targetX}, y: ${targetY}, z: ${targetZ}
      `;
            }

            // Mettre à jour à chaque frame
            requestAnimationFrame(updatePositionDisplay);
        };

        // Démarrer la mise à jour
        updatePositionDisplay();
    };

    const addLight = (sceneRef: any) => {
        lightsRefMain.current = [];
        lightsRefAmbiant.current = [];
        lightsRefSphere.current = [];

        // Lumière principale
        const mainLight = new THREE.DirectionalLight(0xffffff, mainLightIntensity);
        mainLight.position.set(5, 10, 7.5);
        mainLight.castShadow = true;
        mainLight.shadow.bias = -0.0005;
        mainLight.shadow.normalBias = 0.02;
        sceneRef.add(mainLight);
        lightsRefMain.current.push(mainLight);

        // Lumière ambiante forte
        const ambientLight = new THREE.AmbientLight(0xffffff, ambiantLightIntensity);
        sceneRef.add(ambientLight);
        lightsRefAmbiant.current.push(ambientLight);

        const radius = 5;
        const nbLights = 16; // Plus de lumières pour une meilleure couverture
        const intensity = sphereLightIntensity; // Utiliser l'intensité de l'état

        // Positions sur une sphère (phi: latitude, theta: longitude)
        for (let i = 0; i < nbLights; i++) {
            // Distribuer les lumières uniformément sur une sphère
            const phi = Math.acos(-1 + (2 * i) / nbLights);
            const theta = Math.sqrt(nbLights * Math.PI) * phi;

            // Convertir les coordonnées sphériques en cartésiennes
            const x = radius * Math.sin(phi) * Math.cos(theta);
            const y = radius * Math.sin(phi) * Math.sin(theta);
            const z = radius * Math.cos(phi);

            // Créer une lumière ponctuelle (plus adaptée pour un éclairage sphérique)
            const light = new THREE.PointLight(0xffffff, intensity, 20);
            light.position.set(x, y, z);

            sceneRef.add(light);
            lightsRefSphere.current.push(light);
        }
    }



    const loadCarlaModel = () => {
        if (!sceneRef.current) return;

        // Créer un groupe parent pour le modèle Carla
        const carlaGroup = new THREE.Group();
        sceneRef.current.add(carlaGroup);

        // Charger les textures d'abord
        const textureLoader = new THREE.TextureLoader();
        const diffuseMap = textureLoader.load('/assets/model/rp_carla_rigged_001_dif.jpg');
        const normalMap = textureLoader.load('/assets/model/rp_carla_rigged_001_norm.jpg');
        const glossMap = textureLoader.load('/assets/model/rp_carla_rigged_001_gloss.jpg');

        // Créer le matériau avec les textures
        const carlaMaterial = new THREE.MeshStandardMaterial({
            map: diffuseMap,                // Texture de couleur/diffuse
            normalMap: normalMap,           // Texture de normales pour les détails
            roughnessMap: glossMap,         // Utiliser la texture gloss pour la rugosité (inversée)
            roughness: 1,
            metalness: 0,
            envMapIntensity: 0.5
        });


        // Charger le modèle Carla
        loader.load(
            '/assets/model/carla_avant_bras_droits7.fbx',
            (carlaModel: any) => {
                // Ajuster la taille et la position
                carlaModel.scale.set(0.018, 0.018, 0.018);
                carlaModel.position.set(-0.67, -0.97, 1.78);
                carlaModel.rotation.set(0, 3 * Math.PI / 4, 0);

                // Appliquer le matériau avec textures à tous les meshes
                carlaModel.traverse((node: any) => {
                    if (node.isMesh) {
                        node.material = carlaMaterial;
                        node.castShadow = true;
                        node.receiveShadow = true;
                    }
                });

                // Ajouter au groupe
                carlaGroup.add(carlaModel);
                carlaModelRef.current = carlaModel;

                console.log("Modèle Carla chargé avec succès");
            },
            (xhr: any) => {
                console.log((xhr.loaded / xhr.total) * 100 + '% du modèle Carla chargé');
            },
            (error: any) => {
                console.error('Erreur lors du chargement du modèle Carla:', error);
            }
        );
    };

    // Chargement du modèle GLTF du DOZ
    const loadDoz = () => {
        if (!sceneRef.current) return;

        // Créer un groupe parent pour contenir les deux modèles
        const dozGroup = new THREE.Group();
        dozGroupRef.current = dozGroup;
        sceneRef.current.add(dozGroup);


        // Charger le modèle de face avant
        loader.load(
            frontModelUrl,
            (frontModel: any) => {
                frontModelRef.current = frontModel;
                console.log(frontModel)

                frontModel.traverse((child: any) => {
                    if (child.isMesh) {
                        const g = child.geometry;
                        // Important: supprime d’anciennes normales/tangentes et recalcule
                        g.deleteAttribute?.('normal');
                        g.deleteAttribute?.('tangent');
                        g.computeVertexNormals();
                        // Si c’est un BufferGeometry “non indexé”, essaye :
                        // g = g.toNonIndexed(); g.computeVertexNormals();
                    }
                });

                frontModelRef.current.traverse((node: any) => {
                    node.castShadow = true;
                    node.receiveShadow = true;
                    if (node.material) {
                        node.material.envMapIntensity = 1.5;
                    }
                });

                // Ajuster la taille et la position du modèle front
                const frontScale = adjustModelSize(frontModel, false);
                frontModel.position.z -= 0.1;

                // const textureLoader = new THREE.TextureLoader();

                // const colorMap = textureLoader.load('/assets/textures/doz/Poliigon_MetalGalvanizedZinc_7184_BaseColor.jpg');
                // const metalnessMap = textureLoader.load('/assets/textures/doz/Poliigon_MetalGalvanizedZinc_7184_Metallic.jpg');
                // const roughnessMap = textureLoader.load('/assets/textures/doz/Poliigon_MetalGalvanizedZinc_7184_Roughness.jpg');
                // const normalMap = textureLoader.load('/assets/textures/doz/Poliigon_MetalGalvanizedZinc_7184_Normal.png'); // ou NormalGL selon le format
                // const displacementMap = textureLoader.load('/assets/textures/doz/Poliigon_MetalGalvanizedZinc_7184_Displacement.tiff');

                // const dozMaterial = new THREE.MeshPhysicalMaterial({
                //     // map: colorMap,
                //     metalnessMap: metalnessMap,
                //     roughnessMap: roughnessMap,
                //     normalMap: normalMap,
                //     displacementMap: displacementMap,
                //     metalness: 0.1,      // Pour un effet métallique
                //     roughness: 0.2,    // Ajuste selon le rendu souhaité
                //     displacementScale: 0.005, // Ajuste selon le relief voulu
                //     clearcoat: 1,
                //     clearcoatRoughness: 0.2,
                //     emissive: new THREE.Color(frontModelColor),
                //     emissiveIntensity: 0.1,
                //     envMapIntensity: 2.0
                // });
                //
                // const dozMaterialBack = new THREE.MeshPhysicalMaterial({
                //     // map: colorMap,
                //     metalnessMap: metalnessMap,
                //     roughnessMap: roughnessMap,
                //     normalMap: normalMap,
                //     displacementMap: displacementMap,
                //     metalness: 0.1,      // Pour un effet métallique
                //     roughness: 0.2,    // Ajuste selon le rendu souhaité
                //     displacementScale: 0.005, // Ajuste selon le relief voulu
                //     clearcoat: 1,
                //     clearcoatRoughness: 0.2,
                //     emissive: new THREE.Color(backModelColor),
                //     emissiveIntensity: 0.1,
                //     envMapIntensity: 2.0
                // });

                frontModelRef.current.traverse((node: any) => {
                    if (node.isMesh && (node.name === "front")) {
                        // node.material = dozMaterial;
                    }

                    if (node.isMesh && (node.name === "back")) {
                        // node.material = dozMaterialBack;
                    }
                });

                // frontModelRef.current.traverse((node: any) => {
                //     if (node.isMesh && node.material && (node.name === "front" || node.name === "back")) {
                //         node.material = new THREE.MeshPhysicalMaterial({
                //             metalness: 0.1,
                //             roughness: 0.6,
                //         });
                //     }
                // });
                //Ajouter le modèle au groupe parent
                dozGroup.add(frontModel);

                setIsModelLoaded(true);

                console.log("Modèle face avant chargé avec succès, échelle:", frontScale);
            },
            (xhr: any) => {
                console.log((xhr.loaded / xhr.total) * 100 + '% du modèle avant chargé');
            },
            (error: any) => {
                console.error('Une erreur s\'est produite lors du chargement du modèle avant:', error);
            }
        );
    };

    // Chargement du modèle GLTF de la table
    const loadTable = () => {
        if (!sceneRef.current) return;

        // Créer un groupe parent pour contenir les deux modèles
        const tableGroup = new THREE.Group();
        tableGroupRef.current = tableGroup;
        sceneRef.current.add(tableGroup);


        // Charger le modèle de face avant
        loader.load(
            piedModelUrl,
            (piedModel: any) => {
                cleanModel(piedModel);
                piedModelRef.current = piedModel;
                piedModelRef.current.name = 'plateau';

                // Ajuster la taille et la position du modèle front
                const frontScale = adjustModelSize(piedModel, false);

                // Ajouter le modèle au groupe parent
                tableGroup.add(piedModel);

                tableGroupRef.current = tableGroup;

                tableGroup.position.set(0, 0.06, 1)
                tableGroup.rotation.set(0, Math.PI / 4, 0)
                tableGroup.scale.set(1,1.06,1)

                // const textureLoader = new THREE.TextureLoader();

                // const colorMap = textureLoader.load('/assets/textures/doz/Poliigon_MetalGalvanizedZinc_7184_BaseColor.jpg');
                // const metalnessMap = textureLoader.load('/assets/textures/doz/Poliigon_MetalGalvanizedZinc_7184_Metallic.jpg');
                // const roughnessMap = textureLoader.load('/assets/textures/doz/Poliigon_MetalGalvanizedZinc_7184_Roughness.jpg');
                // const normalMap = textureLoader.load('/assets/textures/doz/Poliigon_MetalGalvanizedZinc_7184_Normal.png'); // ou NormalGL selon le format
                // const displacementMap = textureLoader.load('/assets/textures/doz/Poliigon_MetalGalvanizedZinc_7184_Displacement.tiff');
                //
                // const tableMaterialPied = new THREE.MeshPhysicalMaterial({
                //     // map: colorMap,
                //     metalnessMap: metalnessMap,
                //     roughnessMap: roughnessMap,
                //     normalMap: normalMap,
                //     displacementMap: displacementMap,
                //     metalness: 0.1,      // Pour un effet métallique
                //     roughness: 0.2,    // Ajuste selon le rendu souhaité
                //     displacementScale: 0.005, // Ajuste selon le relief voulu
                //     clearcoat: 1,
                //     clearcoatRoughness: 0.2,
                //     emissive: new THREE.Color(frontModelColor),
                //     emissiveIntensity: 0.1,
                //     envMapIntensity: 2.0,
                //     wireframe: false,
                // });
                //
                // const tableMaterialPlateau = new THREE.MeshPhysicalMaterial({
                //     // map: colorMap,
                //     metalnessMap: metalnessMap,
                //     roughnessMap: roughnessMap,
                //     normalMap: normalMap,
                //     displacementMap: displacementMap,
                //     metalness: 0.1,      // Pour un effet métallique
                //     roughness: 0.2,    // Ajuste selon le rendu souhaité
                //     displacementScale: 0.005, // Ajuste selon le relief voulu
                //     clearcoat: 1,
                //     clearcoatRoughness: 0.2,
                //     emissive: new THREE.Color(backModelColor),
                //     emissiveIntensity: 0.1,
                //     envMapIntensity: 2.0,
                //     wireframe: false,
                // });


                tableGroupRef.current.traverse((node: any) => {
                    if (node.isMesh && node.name === "pied") {
                        // node.material = tableMaterialPied;
                        node.material.color.set(piedModelColor);
                    }
                    if (node.isMesh && node.name === "plateau") {
                        // node.material = tableMaterialPlateau;
                        node.material.color.set(plateauModelColor);
                    }
                });

                console.log("Modèle pied avant chargé avec succès, échelle:", frontScale);
            },
            (xhr: any) => {
                console.log((xhr.loaded / xhr.total) * 100 + '% du modèle avant chargé');
            },
            (error: any) => {
                console.error('Une erreur s\'est produite lors du chargement du modèle avant:', error);
            }
        );
    };

    const cleanModel = (model: any) => {
        const objectsToRemove: any[] = [];

        model.traverse((child: any) => {
            if (child.isMesh) {
                // Nettoyer les matériaux
                if (child.material) {
                    if (Array.isArray(child.material)) {
                        child.material.forEach((mat: any) => {
                            mat.wireframe = false;
                        });
                    } else {
                        child.material.wireframe = false;
                    }
                }

                // Optimiser la géométrie
                if (child.geometry) {
                    child.geometry.computeVertexNormals();
                }
            }

            // Marquer les lignes pour suppression
            if (child.type === 'LineSegments' || child.type === 'Line' || child.type === 'LineLoop') {
                objectsToRemove.push(child);
            }
        });

        // Supprimer les lignes
        objectsToRemove.forEach(obj => {
            if (obj.parent) {
                obj.parent.remove(obj);
            }
        });
    };

    // Fonction pour ajuster la taille d'un modèle
    const adjustModelSize = (model: any, isBackModel = false) => {
        // Calculer la boîte englobante du modèle
        const box = new THREE.Box3().setFromObject(model);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());

        // Normaliser la taille du modèle
        let scale = 2 / Math.max(size.x, size.y, size.z);

        // Si c'est le modèle arrière, réduire l'échelle de moitié
        if (isBackModel) {
            scale *= 0.68;
        }

        model.scale.set(scale, scale, scale);

        // Centrer le modèle en compensant son offset
        model.position.x = -center.x * scale;
        model.position.y = -center.y * scale;
        model.position.z = -center.z * scale;

        // Désactiver la frustum culling pour éviter la disparition du modèle
        model.traverse(function (obj: any) {
            if (obj.isMesh) {
                obj.frustumCulled = false;
                obj.castShadow = true;
                obj.receiveShadow = true;
            }
        });

        return scale;
    };

    // Mettre à jour l'aperçu du logo
    const updateLogoPreview = (imageUrl: string) => {
        const preview = document.getElementById('logo-preview');
        if (preview) {
            preview.innerHTML = '';
            const img = document.createElement('img');
            img.src = imageUrl;
            img.style.maxWidth = '90%';
            img.style.maxHeight = '90%';
            preview.appendChild(img);
        }
    };

    // Gestion de l'upload de logo
    const handleLogoUpload = (e: any) => {
        if (e.target.files && e.target.files[0]) {
            console.log("Fichier sélectionné:", e.target.files[0].name);

            const reader = new FileReader();
            reader.onload = (event) => {
                if (event.target?.result) {
                    console.log("Fichier chargé avec succès");
                    const logoUrl = event.target.result.toString();

                    // Prétraiter l'image pour enlever l'arrière-plan
                    processLogoImage(logoUrl, (processedImageUrl: string) => {
                        applyLogoDecal(processedImageUrl);
                    });
                }
            };
            reader.onerror = (err) => {
                console.error("Erreur lors de la lecture du fichier:", err);
            };
            reader.readAsDataURL(e.target.files[0]);
        }
    };

    if (!isClient) {
        return <div>Chargement...</div>;
    }

    return (
        <div className="flex h-screen">
            <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css"/>
            <div className="w-80 bg-gray-100 p-5 shadow-md overflow-y-auto">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Configurateur DOZ</h2>

                {/* Champ pour le nom du client */}
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nom du client
                    </label>
                    <input
                        type="text"
                        className="w-full px-3 py-2 border text-black border-gray-300 rounded-md"
                        placeholder="Entrez le nom du client"
                        value={clientName}
                        onChange={(e) => setClientName(e.target.value)}
                    />
                </div>

                {/* Boutons de sauvegarde et chargement */}
                <div className="mb-6 flex space-x-2">
                    <button
                        className="flex-1 bg-green-500 text-white py-2 px-3 rounded"
                        onClick={() => setShowSaveModal(true)}
                    >
                        <i className="fas fa-save mr-1"></i> Sauvegarder
                    </button>
                    <button
                        className="flex-1 bg-blue-500 text-white py-2 px-3 rounded"
                        onClick={() => document.getElementById('load-config-dropdown')?.classList.toggle('hidden')}
                    >
                        <i className="fas fa-folder-open mr-1"></i> Charger
                    </button>
                </div>

                {/* Liste déroulante des configurations sauvegardées */}
                <div id="load-config-dropdown"
                     className="mb-6 bg-white border border-gray-300 rounded-md shadow-md p-2 hidden text-black">
                    <h3 className="text-lg font-semibold mb-2">Configurations sauvegardées</h3>
                    {savedConfigurations.length === 0 ? (
                        <p className="text-gray-500 text-center py-2">Aucune configuration sauvegardée</p>
                    ) : (
                        <div className="max-h-60 overflow-y-auto">
                            {savedConfigurations.map((config, index) => (
                                <div key={index} className="p-2 border-b border-gray-200 last:border-b-0">
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <div className="font-medium">{config.name}</div>
                                            <div className="text-sm text-gray-500">Client: {config.clientName}</div>
                                            <div className="text-xs text-gray-400">{config.date}</div>
                                        </div>
                                        <div className="flex space-x-1">
                                            <button
                                                className="text-blue-500 hover:text-blue-700"
                                                onClick={() => loadConfiguration(config)}
                                                title="Charger cette configuration"
                                            >
                                                <i className="fas fa-download"></i>
                                            </button>
                                            <button
                                                className="text-red-500 hover:text-red-700"
                                                onClick={() => deleteConfiguration(index)}
                                                title="Supprimer cette configuration"
                                            >
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Modal de sauvegarde */}
                {showSaveModal && (
                    <div
                        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 text-black">
                        <div className="bg-white p-6 rounded-lg shadow-lg w-80">
                            <h3 className="text-lg font-bold mb-4">Sauvegarder la configuration</h3>
                            <input
                                type="text"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md mb-4"
                                placeholder="Nom de la configuration"
                                value={configName}
                                onChange={(e) => setConfigName(e.target.value)}
                            />
                            <div className="flex justify-end space-x-2">
                                <button
                                    className="px-4 py-2 bg-gray-300 rounded"
                                    onClick={() => setShowSaveModal(false)}
                                >
                                    Annuler
                                </button>
                                <button
                                    className="px-4 py-2 bg-green-500 text-white rounded"
                                    onClick={saveCurrentConfiguration}
                                    disabled={!configName}
                                >
                                    Sauvegarder
                                </button>
                            </div>
                        </div>
                    </div>
                )}


                <div className="mb-6">
                    <h3 className="text-lg text-black font-semibold mb-2">Couleur de l&apos;avant</h3>
                    <div className="relative">
                        <button
                            className="w-full flex items-center justify-between text-black bg-white border border-gray-300 rounded px-3 py-2"
                            onClick={() => openColorSelector('front')}
                        >
                            <div className="flex items-center">
                                <div
                                    className="w-6 h-6 rounded-full mr-2"
                                    style={{backgroundColor: frontModelColor}}
                                />
                                <span>{Object.values(colors).find(c => c.hex === frontModelColor)?.name || 'Couleur personnalisée'}</span>
                            </div>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                 xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
                            </svg>
                        </button>
                    </div>
                </div>

                <div className="mb-6">
                    <h3 className="text-lg text-black font-semibold mb-2">Couleur de l&apos;arrière</h3>
                    <div className="relative">
                        <button
                            className="w-full flex items-center justify-between text-black bg-white border border-gray-300 rounded px-3 py-2"
                            onClick={() => openColorSelector('back')}
                        >
                            <div className="flex items-center">
                                <div
                                    className="w-6 h-6 rounded-full mr-2"
                                    style={{backgroundColor: backModelColor}}
                                />
                                <span>{Object.values(colors).find(c => c.hex === backModelColor)?.name || 'Couleur personnalisée'}</span>
                            </div>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                 xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
                            </svg>
                        </button>
                    </div>
                </div>

                <hr/>
                <h2 className="text-xl font-bold text-gray-800 mb-4">Configurateur Table</h2>

                <div className="mb-6">
                    <h3 className="text-lg text-black font-semibold mb-2">Couleur du pied</h3>
                    <div className="relative">
                        <button
                            className="w-full flex items-center justify-between text-black bg-white border border-gray-300 rounded px-3 py-2"
                            onClick={() => openColorSelector('pied')}
                        >
                            <div className="flex items-center">
                                <div
                                    className="w-6 h-6 rounded-full mr-2"
                                    style={{backgroundColor: piedModelColor}}
                                />
                                <span>{Object.values(colors).find(c => c.hex === piedModelColor)?.name || 'Couleur personnalisée'}</span>
                            </div>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                 xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
                            </svg>
                        </button>
                    </div>
                </div>

                <div className="mb-6">
                    <h3 className="text-lg text-black font-semibold mb-2">Couleur du plateau</h3>
                    <div className="relative">
                        <button
                            className="w-full flex items-center justify-between text-black bg-white border border-gray-300 rounded px-3 py-2"
                            onClick={() => openColorSelector('plateau')}
                        >
                            <div className="flex items-center">
                                <div
                                    className="w-6 h-6 rounded-full mr-2"
                                    style={{backgroundColor: plateauModelColor}}
                                />
                                <span>{Object.values(colors).find(c => c.hex === plateauModelColor)?.name || 'Couleur personnalisée'}</span>
                            </div>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                 xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Dropdown pour la sélection de couleur */}
                {isColorDropdownOpen && (
                    <div
                        ref={colorDropdownRef}
                        className="absolute top-56 left-80 z-10 mt-1 w-72 bg-white border border-gray-300 rounded-md shadow-lg"
                    >
                        <div className="p-2">
                            <input
                                type="text"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md mb-2"
                                placeholder="Rechercher une couleur..."
                                value={colorSearchTerm}
                                onChange={(e) => setColorSearchTerm(e.target.value)}
                            />
                            <div className="max-h-60 overflow-y-auto">
                                {filteredColors.map((color) => (
                                    <div
                                        key={color.code}
                                        className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
                                        onClick={() => selectColor(color.hex)}
                                    >
                                        <div
                                            className="w-6 h-6 rounded-full mr-2"
                                            style={{backgroundColor: color.hex}}
                                        />
                                        <div>
                                            <div className="font-medium">{color.name}</div>
                                            <div
                                                className="text-xs text-gray-500">Code: {color.code} | {color.hex}</div>
                                        </div>
                                    </div>
                                ))}
                                {filteredColors.length === 0 && (
                                    <div className="p-2 text-center text-gray-500">
                                        Aucune couleur trouvée
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <hr/>
                <h2 className="text-xl font-bold text-gray-800 mb-4">Configuration du logo</h2>
                <div className="mb-6">
                    <label
                        className={`bg-blue-600 text-white py-2 px-4 rounded block text-center`}>
                        Choisir un logo
                        <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleLogoUpload}
                        />
                    </label>
                    <div id="logo-preview"
                         className="mt-3 h-24 bg-black border text-black border-gray-300 flex items-center justify-center">
                        <span>Aperçu du logo</span>
                    </div>
                </div>

                <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-2 text-black">Paramètres du logo</h3>

                    <div className="mb-3">
                        <label className="block mb-1 text-black">
                            Taille: {logoScale.toFixed(1)}
                        </label>
                        <input
                            type="range"
                            min="0.1"
                            max="1.5"
                            step="0.1"
                            value={logoScale}
                            onChange={(e) => setLogoScale(parseFloat(e.target.value))}
                            className="w-full"
                        />
                    </div>

                    <div className="mb-3">
                        <label className="block mb-1 text-black">
                            Position X: {logoPositionX.toFixed(1)}
                        </label>
                        <input
                            type="range"
                            min="-1"
                            max="1"
                            step="0.1"
                            value={logoPositionX}
                            onChange={(e) => setLogoPositionX(parseFloat(e.target.value))}
                            className="w-full"
                        />
                    </div>

                    <div className="mb-3">
                        <label className="block mb-1 text-black">
                            Seuil blanc: {logoThreshold}
                        </label>
                        <input
                            type="range"
                            min="150"
                            max="250"
                            step="5"
                            value={logoThreshold}
                            onChange={(e) => setLogoThreshold(parseInt(e.target.value))}
                            className="w-full"
                        />
                        <div className="text-xs text-gray-500 mt-1">
                            Plus la valeur est basse, plus de pixels seront considérés comme partie du logo
                        </div>
                    </div>

                    <hr/>
                    <h2 className="text-xl font-bold text-gray-800 mb-4">Visibilité des modèles</h2>

                    <div className="mb-4 space-y-3">
                        <div className="flex items-center justify-between">
                            <label className="text-lg text-black font-semibold">DOZ principal</label>
                            <button
                                onClick={() => setShowMainDoz(!showMainDoz)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${showMainDoz ? 'bg-blue-500' : 'bg-gray-300'}`}
                            >
      <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${showMainDoz ? 'translate-x-5' : 'translate-x-1'}`}
      />
                            </button>
                        </div>

                        <div className="flex items-center justify-between">
                            <label className="text-lg text-black font-semibold">DOZ gauche</label>
                            <button
                                onClick={() => setShowLeftDoz(!showLeftDoz)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${showLeftDoz ? 'bg-blue-500' : 'bg-gray-300'}`}
                            >
      <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${showLeftDoz ? 'translate-x-5' : 'translate-x-1'}`}
      />
                            </button>
                        </div>

                        <div className="flex items-center justify-between">
                            <label className="text-lg text-black font-semibold">DOZ droite</label>
                            <button
                                onClick={() => setShowRightDoz(!showRightDoz)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${showRightDoz ? 'bg-blue-500' : 'bg-gray-300'}`}
                            >
      <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${showRightDoz ? 'translate-x-5' : 'translate-x-1'}`}
      />
                            </button>
                        </div>

                        <div className="flex items-center justify-between">
                            <label className="text-lg text-black font-semibold">Table</label>
                            <button
                                onClick={() => setShowTable(!showTable)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${showTable ? 'bg-blue-500' : 'bg-gray-300'}`}
                            >
      <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${showTable ? 'translate-x-5' : 'translate-x-1'}`}
      />
                            </button>
                        </div>

      {/*                  <div className="flex items-center justify-between">*/}
      {/*                      <label className="text-lg text-black font-semibold">Fond</label>*/}
      {/*                      <button*/}
      {/*                          onClick={() => setShowEnvMap(!showEnvMap)}*/}
      {/*                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${showEnvMap ? 'bg-blue-500' : 'bg-gray-300'}`}*/}
      {/*                      >*/}
      {/*<span*/}
      {/*    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${showEnvMap ? 'translate-x-5' : 'translate-x-1'}`}*/}
      {/*/>*/}
      {/*                      </button>*/}
      {/*                  </div>*/}

                        <div className="flex items-center justify-between">
                            <label className="text-lg text-black font-semibold">Personnage</label>
                            <button
                                onClick={() => setShowPersonnage(!showPersonnage)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${showPersonnage ? 'bg-blue-500' : 'bg-gray-300'}`}
                            >
      <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${showPersonnage ? 'translate-x-5' : 'translate-x-1'}`}
      />
                            </button>
                        </div>

                        <div className="flex items-center justify-between">
                            <label className="text-lg text-black font-semibold">Logos</label>
                            <button
                                onClick={() => setShowLogos(!showLogos)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${showLogos ? 'bg-blue-500' : 'bg-gray-300'}`}
                            >
      <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${showLogos ? 'translate-x-5' : 'translate-x-1'}`}
      />
                            </button>
                        </div>
                    </div>


                    <hr/>
                    <h2 className="text-xl font-bold text-gray-800 mb-4">Éclairage</h2>

                    <div className="mb-4 space-y-3">
                        <div className="flex items-center justify-between">
                            <label className="text-lg text-black font-semibold">Lumière principale</label>
                            <button
                                onClick={() => setMainLightsEnabled(!mainLightsEnabled)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${mainLightsEnabled ? 'bg-blue-500' : 'bg-gray-300'}`}
                            >
            <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${mainLightsEnabled ? 'translate-x-5' : 'translate-x-1'}`}
            />
                            </button>
                        </div>

                        <div className="mb-3">
                            <label className="block mb-1 text-black">
                                Intensité: {mainLightIntensity.toFixed(1)}
                            </label>
                            <input
                                type="range"
                                min="0.1"
                                max="5"
                                step="0.1"
                                value={mainLightIntensity}
                                onChange={(e) => setMainLightIntensity(parseFloat(e.target.value))}
                                className="w-full"
                                disabled={!mainLightsEnabled}
                            />
                        </div>
                    </div>

                    <div className="mb-4 space-y-3">
                        <div className="flex items-center justify-between">
                            <label className="text-lg text-black font-semibold">Lumières ambiante</label>
                            <button
                                onClick={() => setAmbiantLightsEnabled(!mainLightsEnabled)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${mainLightsEnabled ? 'bg-blue-500' : 'bg-gray-300'}`}
                            >
            <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${mainLightsEnabled ? 'translate-x-5' : 'translate-x-1'}`}
            />
                            </button>
                        </div>

                        <div className="mb-3">
                            <label className="block mb-1 text-black">
                                Intensité: {ambiantLightIntensity.toFixed(1)}
                            </label>
                            <input
                                type="range"
                                min="0.1"
                                max="5"
                                step="0.1"
                                value={ambiantLightIntensity}
                                onChange={(e) => setAmbiantLightIntensity(parseFloat(e.target.value))}
                                className="w-full"
                                disabled={!ambiantLightsEnabled}
                            />
                        </div>
                    </div>

                    <div className="mb-4 space-y-3">
                        <div className="flex items-center justify-between">
                            <label className="text-lg text-black font-semibold">Sphère de lumière</label>
                            <button
                                onClick={() => setSphereLightsEnabled(!sphereLightsEnabled)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${sphereLightsEnabled ? 'bg-blue-500' : 'bg-gray-300'}`}
                            >
            <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${sphereLightsEnabled ? 'translate-x-5' : 'translate-x-1'}`}
            />
                            </button>
                        </div>

                        <div className="mb-3">
                            <label className="block mb-1 text-black">
                                Intensité: {sphereLightIntensity.toFixed(1)}
                            </label>
                            <input
                                type="range"
                                min="0.1"
                                max="5"
                                step="0.1"
                                value={sphereLightIntensity}
                                onChange={(e) => setSphereLightIntensity(parseFloat(e.target.value))}
                                className="w-full"
                                disabled={!sphereLightsEnabled}
                            />
                        </div>
                    </div>
                </div>

            </div>

            <div ref={viewerRef} className="flex-1 bg-gray-200"/>
        </div>
    );
}